/**
 * domains/market/engines/setup2/schema.ts — MER Algo Footprints v7.1H
 *
 * Zod v3 schema for RichSetup — post-assembly validation.
 */

import { z } from 'zod';

export const SETUP_ENGINE_VERSION = 'setup2-v1.1';
export const SCORING_VERSION      = 'scoring-v1.1';
export const SCHEMA_VERSION       = 'setup2-schema-v1.1';

const score    = z.number().min(0).max(100);
const nonEmpty = z.string().min(1);
const nullNum  = z.number().nullable();

const SetupClassSchema = z.enum([
  'CONTINUATION','REVERSAL','TRAP_REVERSAL',
  'LIQUIDITY_SWEEP','BREAKOUT_CONFIRMATION',
  'MEAN_REVERSION','NO_VALID_SETUP',
]);
const SetupStateSchema     = z.enum(['DETECTED','QUALIFIED','DEVELOPING','READY','ACTIVE','INVALIDATED','EXPIRED']);
const SetupDirectionSchema = z.enum(['LONG','SHORT','NEUTRAL']);

export const RichSetupSchema = z.object({
  setup_id:             nonEmpty,
  symbol:               nonEmpty,
  timeframe:            nonEmpty,
  timestamp:            z.number().positive(),
  setup_classification: SetupClassSchema,
  setup_name:           nonEmpty,
  setup_state:          SetupStateSchema,
  direction:            SetupDirectionSchema,
  setup_score:          score,
  readiness_score:      score,

  quality_breakdown: z.object({
    structure_score:         score,
    context_score:           score,
    liquidity_score:         score,
    trigger_score:           score,
    risk_reward_score:       score,
    execution_quality_score: score,
    raw_composite:           score,
    total_penalty:           z.number().min(0),
    setup_score:             score,
  }),

  readiness_breakdown: z.object({
    trigger_confirmed:     z.boolean(),
    confirmation_present:  z.boolean(),
    execution_zone_active: z.boolean(),
    invalidation_clear:    z.boolean(),
    rr_viable:             z.boolean(),
    timing_quality:        z.enum(['good','marginal','poor']),
    readiness_score:       score,
  }),

  detected_signals:    z.array(z.string()),
  supporting_factors:  z.array(z.string()),
  conflicting_factors: z.array(z.string()),
  penalties:     z.array(z.object({ reason: nonEmpty, points: z.number().positive() })),
  disqualifiers: z.array(z.object({ rule: nonEmpty, reason: nonEmpty, fatal: z.boolean() })),

  entry_model: z.object({
    trigger:           nonEmpty,
    entry_zone_low:    nullNum,
    entry_zone_high:   nullNum,
    entry_price_ideal: nullNum,
    confirmation:      nonEmpty,
    timing_note:       z.string().nullable(),
  }),

  risk_model: z.object({
    stop_level:         nullNum,
    stop_basis:         nonEmpty,
    invalidation_event: nonEmpty,
    stop_quality:       score,
    stop_note:          z.string().nullable(),
  }),

  targets: z.object({
    target_1:     nullNum,
    target_2:     nullNum,
    risk_reward:  nullNum,
    target_basis: nonEmpty,
  }),

  actionability: z.object({
    is_actionable:    z.boolean(),
    can_become_ready: z.boolean(),
    blocking_disqs:   z.array(z.string()),
    missing_items:    z.array(z.string()),
  }),

  audit: z.object({
    setup_id:        nonEmpty,
    detected_at:     z.number().positive(),
    engine_version:  nonEmpty,
    scoring_version: z.string(),
    schema_version:  z.string(),
    input_hash:      z.string(),
    scoring_weights: z.object({
      structure:         z.number(),
      context:           z.number(),
      liquidity:         z.number(),
      trigger:           z.number(),
      risk_reward:       z.number(),
      execution_quality: z.number(),
    }),
    threshold_snapshot: z.object({
      min_setup_score:     z.number(),
      min_readiness_score: z.number(),
      min_rr:              z.number(),
      max_trap_severity:   z.string(),
    }),
    signal_inputs:     z.record(z.unknown()),
    detection_trace:   z.array(z.object({
      rule_id:    z.string(),
      rule_fired: z.boolean(),
      conditions: z.array(z.string()),
      reason:     z.string(),
    })),
    state_history:     z.array(SetupStateSchema),
    state_transitions: z.array(z.object({ state: SetupStateSchema, reason: z.string() })),
  }),
});

export type ValidatedRichSetup = z.infer<typeof RichSetupSchema>;

export function validateRichSetup(setup: unknown): {
  ok: true;  data: ValidatedRichSetup;
} | {
  ok: false; errors: string[];
} {
  const result = RichSetupSchema.safeParse(setup);
  if (result.success) return { ok: true, data: result.data };
  return { ok: false, errors: result.error.issues.map(i => `${i.path.join('.')}: ${i.message}`) };
}
